import { Icon, NonIdealState } from "@blueprintjs/core";
import { useGetPostListNameIdQuery } from "@package/ui-gql/index-data-access";
import moment from "moment";
import { useMemo } from "react";
import { useGetEventStats } from "../../../../hooks/use-get-event-stats";
import {
  fillDailyTrend,
  formatDurationShort,
  getLastSevenDayKeys,
  getLastSevenDaysRange,
  getNiceAxisMax,
} from "../../../../utils/analytics-format-utils";
import { DashboardCard } from "../../../DashboardCard/DashboardCard";

const AXIS_TICKS = [1, 0.75, 0.5, 0.25, 0];

export function LocationAnalyticsDrilldown({
  location,
}: {
  location?: string;
}) {
  const range = useMemo(() => getLastSevenDaysRange(), []);
  const days = useMemo(() => getLastSevenDayKeys(), []);

  const variablesStats = useMemo(
    () => ({ filter: { location, range } }),
    [location, range],
  );
  const { data: dataStats, loading: loadingStats } = useGetEventStats(
    variablesStats,
    !location,
  );
  const { data: dataPosts } = useGetPostListNameIdQuery({
    variables: { page: 0, perPage: 1000 },
  });

  const stats = dataStats?.item;
  const dailyTrend = useMemo(
    () => fillDailyTrend(stats?.dailyTrend ?? [], days),
    [stats?.dailyTrend, days],
  );
  const maxCount = Math.max(1, ...dailyTrend.map((bucket) => bucket.count));
  const axisMax = getNiceAxisMax(maxCount);
  const hasActivity =
    dailyTrend.some((bucket) => bucket.count > 0) || !!stats?.topContent.length;

  if (!location) {
    return null;
  }

  if (!loadingStats && !hasActivity) {
    return (
      <DashboardCard className="mt-4">
        <NonIdealState
          icon="timeline-bar-chart"
          title="No activity yet"
          description="No content activity recorded for this location in the last 7 days."
        />
      </DashboardCard>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
      <DashboardCard className="h-full flex flex-col">
        <div className="mb-3 shrink-0">
          <p className="text-sm font-semibold text-brand-ink">
            Engagement by day
          </p>
          <p className="text-xs text-brand-muted">Content opens</p>
        </div>
        <div className="flex flex-1 min-h-[100px] gap-2">
          <div className="flex flex-col justify-between items-end w-8 shrink-0 text-[10px] text-brand-muted py-0.5">
            {AXIS_TICKS.map((tick) => (
              <span key={tick}>{Math.round(axisMax * tick)}</span>
            ))}
          </div>
          <div className="flex items-end gap-[7px] flex-1 px-1 border-b border-brand-line bg-[repeating-linear-gradient(var(--color-brand-line)_0_1px,transparent_1px_33px)]">
            {dailyTrend.map((bucket) => (
              <div
                key={bucket.date}
                className="flex-1 flex flex-col items-center justify-end h-full"
                title={`${bucket.count} on ${moment(bucket.date).format("MMM D, YYYY")}`}
              >
                <div
                  className="w-full rounded-t-[3px] bg-gradient-to-b from-brand-chart to-brand-chart-dark min-h-[3px]"
                  style={{ height: `${(bucket.count / axisMax) * 100}%` }}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-2 mt-2 shrink-0">
          <div className="w-8 shrink-0" />
          <div className="flex gap-[7px] flex-1 px-1">
            {dailyTrend.map((bucket) => (
              <div
                key={bucket.date}
                className="flex-1 flex flex-col items-center"
              >
                <span className="text-[11px] font-semibold text-brand-ink">
                  {moment(bucket.date).format("ddd")}
                </span>
                <span className="text-[10px] text-brand-muted">
                  {moment(bucket.date).format("M/D")}
                </span>
              </div>
            ))}
          </div>
        </div>
      </DashboardCard>

      <DashboardCard className="h-full flex flex-col">
        <div className="mb-3 shrink-0">
          <p className="text-sm font-semibold text-brand-ink">Top content</p>
          <p className="text-xs text-brand-muted">
            Opens · avg. time · QR scans
          </p>
        </div>
        {stats?.topContent.length ? (
          <div className="flex flex-col gap-1">
            {stats.topContent.map((content, index) => {
              const post = dataPosts?.items?.find((p) => p.id === content.post);
              const barPct = Math.round(
                (content.opens /
                  Math.max(1, stats.topContent[0]?.opens ?? content.opens)) *
                  100,
              );

              return (
                <div
                  key={content.post}
                  className="flex items-center gap-3 rounded-lg px-2 py-2 -mx-2 hover:bg-brand-paper"
                >
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-green-tint text-xs font-semibold text-brand-green-dark">
                    {index + 1}
                  </div>
                  <div className="min-w-0 grow">
                    <p className="truncate text-sm font-medium text-brand-ink">
                      {post?.title ?? "Untitled"}
                    </p>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-brand-line">
                      <div
                        className="h-full rounded-full bg-brand-green-dark"
                        style={{ width: `${barPct}%` }}
                      />
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold text-brand-ink">
                      {content.opens} opens
                    </p>
                    <p className="mt-0.5 flex items-center justify-end gap-2 text-xs text-brand-muted">
                      <span className="inline-flex items-center gap-1">
                        <Icon icon="time" size={10} />
                        {formatDurationShort(content.avgInteractionSeconds)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Icon icon="barcode" size={10} />
                        {content.qrScans}
                      </span>
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-brand-muted">
            No content activity recorded for this location in the last 7 days.
          </p>
        )}
      </DashboardCard>
    </div>
  );
}
